import { useState, useEffect, useRef, useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import {
  Search, BookOpen, Package, FileText, LayoutGrid,
  ArrowUpRight, CornerDownLeft, X
} from 'lucide-react';

interface ResultItem {
  id: string;
  label: string;
  sublabel?: string;
  category: 'Ledgers' | 'Stock Items' | 'Masters' | 'Vouchers' | 'Reports' | 'Menus';
  run: () => void;
}

const CATEGORY_ICONS: Record<string, any> = {
  'Ledgers': BookOpen,
  'Stock Items': Package,
  'Masters': LayoutGrid,
  'Vouchers': FileText,
  'Reports': FileText,
  'Menus': LayoutGrid,
};

interface Props {
  pushMenu: (level: string, currentIndex?: number, entityId?: number | null) => void;
}

export const QuickAccessSearch = ({ pushMenu }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<ResultItem[]>([]);
  const activeIndexRef = useRef(0);

  const activeCompany = useStore((state) => state.activeCompany);
  const setCurrentVoucherType = useStore((state) => state.setCurrentVoucherType);

  const dbLedgers = useLiveQuery(
    () => activeCompany ? db.ledgers.where('companyId').equals(activeCompany.id!).toArray() : [],
    [activeCompany]
  );
  const dbStockItems = useLiveQuery(
    () => activeCompany ? db.stockItems.where('companyId').equals(activeCompany.id!).toArray() : [],
    [activeCompany]
  );

  const close = () => {
    setIsOpen(false);
    setQuery('');
    setActiveIndex(0);
  };

  // Global F1 listener — capture phase so it runs before the main navigation handler.
  // Arrow/Enter/Escape are also handled here (and stopped) so the background
  // navigation handler never reacts to keys meant for this popup.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F1' && !e.altKey && !e.ctrlKey && !e.metaKey) {
        if (!useStore.getState().activeCompany) return; // locked state — F1 stays unbound
        e.preventDefault();
        e.stopPropagation();
        setIsOpen((prev) => !prev);
        return;
      }
      if (!isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setActiveIndex((prev) => Math.min(prev + 1, resultsRef.current.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setActiveIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        const item = resultsRef.current[activeIndexRef.current];
        if (item) runItem(item);
      }
    };
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      setQuery('');
      setActiveIndex(0);
    }
  }, [isOpen]);

  // Static, navigable items — all use existing pushMenu/navigation logic (no new business logic)
  const staticItems: ResultItem[] = useMemo(() => [
    // Menus
    { id: 'm-dashboard', label: 'Dashboard / Business Overview', category: 'Menus', run: () => pushMenu('dashboard') },
    { id: 'm-employees', label: 'Employees', category: 'Menus', run: () => pushMenu('employees') },
    { id: 'm-payroll', label: 'Payroll', category: 'Menus', run: () => pushMenu('payroll') },
    { id: 'm-quotations', label: 'Quotations', category: 'Menus', run: () => pushMenu('quotation_list') },
    { id: 'm-customization', label: 'Customization', category: 'Menus', run: () => pushMenu('customization') },
    { id: 'm-binbox', label: 'Bin Box (Deleted Vouchers)', category: 'Menus', run: () => pushMenu('bin_box') },
    { id: 'm-select-company', label: 'Select / Change Company', category: 'Menus', run: () => pushMenu('select_company') },
    { id: 'm-backup', label: 'Backup Company', category: 'Menus', run: () => pushMenu('backup_company_process') },
    { id: 'm-restore', label: 'Restore Company', category: 'Menus', run: () => pushMenu('restore_company') },

    // Vouchers
    { id: 'v-sales', label: 'Sales Voucher', sublabel: 'F8', category: 'Vouchers', run: () => { setCurrentVoucherType('Sales'); pushMenu('vouchers'); } },
    { id: 'v-purchase', label: 'Purchase Voucher', sublabel: 'F9', category: 'Vouchers', run: () => { setCurrentVoucherType('Purchase'); pushMenu('vouchers'); } },
    { id: 'v-payment', label: 'Payment Voucher', sublabel: 'F5', category: 'Vouchers', run: () => { setCurrentVoucherType('Payment'); pushMenu('vouchers'); } },
    { id: 'v-receipt', label: 'Receipt Voucher', sublabel: 'F6', category: 'Vouchers', run: () => { setCurrentVoucherType('Receipt'); pushMenu('vouchers'); } },
    { id: 'v-contra', label: 'Contra Voucher', sublabel: 'F4', category: 'Vouchers', run: () => { setCurrentVoucherType('Contra'); pushMenu('vouchers'); } },
    { id: 'v-journal', label: 'Journal Voucher', sublabel: 'F7', category: 'Vouchers', run: () => { setCurrentVoucherType('Journal'); pushMenu('vouchers'); } },

    // Masters
    { id: 'cm-ledger', label: 'Create Ledger', category: 'Masters', run: () => pushMenu('creation_ledger') },
    { id: 'cm-group', label: 'Create Group', category: 'Masters', run: () => pushMenu('creation_group') },
    { id: 'cm-stockitem', label: 'Create Stock Item', category: 'Masters', run: () => pushMenu('creation_stockitem') },
    { id: 'cm-stockgroup', label: 'Create Stock Group', category: 'Masters', run: () => pushMenu('creation_stockgroup') },
    { id: 'cm-stockcategory', label: 'Create Stock Category', category: 'Masters', run: () => pushMenu('creation_stockcategory') },
    { id: 'cm-unit', label: 'Create Unit', category: 'Masters', run: () => pushMenu('creation_unit') },
    { id: 'cm-currency', label: 'Create Currency', category: 'Masters', run: () => pushMenu('creation_currency') },
    { id: 'cm-location', label: 'Create Location', category: 'Masters', run: () => pushMenu('creation_location') },
    { id: 'cm-vouchertype', label: 'Create Voucher Type', category: 'Masters', run: () => pushMenu('creation_vouchertype') },
    { id: 'am-ledger', label: 'Alter Ledger', category: 'Masters', run: () => pushMenu('alteration_ledger') },
    { id: 'am-group', label: 'Alter Group', category: 'Masters', run: () => pushMenu('alteration_group') },
    { id: 'lm-ledgers', label: 'List of Ledgers', category: 'Masters', run: () => pushMenu('ledgers') },

    // Reports
    { id: 'r-balancesheet', label: 'Balance Sheet', category: 'Reports', run: () => pushMenu('balancesheet') },
    { id: 'r-pnl', label: 'Profit & Loss A/c', category: 'Reports', run: () => pushMenu('profitloss') },
    { id: 'r-daybook', label: 'Day Book', category: 'Reports', run: () => pushMenu('daybook') },
    { id: 'r-trialbalance', label: 'Trial Balance', category: 'Reports', run: () => pushMenu('display_trialbalance') },
    { id: 'r-cashflow', label: 'Cash Flow', category: 'Reports', run: () => pushMenu('display_cashflow') },
    { id: 'r-fundsflow', label: 'Funds Flow', category: 'Reports', run: () => pushMenu('display_fundsflow') },
    { id: 'r-ratioanalysis', label: 'Ratio Analysis', category: 'Reports', run: () => pushMenu('ratioanalysis') },
    { id: 'r-stocksummary', label: 'Stock Summary', category: 'Reports', run: () => pushMenu('display_stocksummary') },
    { id: 'r-gstr1', label: 'GSTR-1 (Outward Supplies)', category: 'Reports', run: () => pushMenu('gstr1') },
    { id: 'r-gstr3b', label: 'GSTR-3B (Summary Return)', category: 'Reports', run: () => pushMenu('gstr3b') },
    { id: 'r-gst-summary', label: 'GST Tax Rate Summary', category: 'Reports', run: () => pushMenu('gst_summary') },
    { id: 'r-party-reg', label: 'Party-wise Sales / Purchase Register', category: 'Reports', run: () => pushMenu('party_register') },
    { id: 'r-display', label: 'Display More Reports', category: 'Reports', run: () => pushMenu('display') },
  ], [pushMenu, setCurrentVoucherType]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();

    const ledgerItems: ResultItem[] = (dbLedgers || [])
      .filter((l) => !l.isDeleted)
      .map((l) => ({
        id: `ledger-${l.id}`,
        label: l.name,
        sublabel: 'Ledger',
        category: 'Ledgers' as const,
        run: () => pushMenu('ledgervouchers', undefined, l.id!),
      }));

    const stockItems: ResultItem[] = (dbStockItems || [])
      .filter((s) => !s.isDeleted)
      .map((s) => ({
        id: `stockitem-${s.id}`,
        label: s.name,
        sublabel: 'Stock Item',
        category: 'Stock Items' as const,
        run: () => pushMenu('stockitemvouchers', undefined, s.id!),
      }));

    const allItems = [...ledgerItems, ...stockItems, ...staticItems];

    if (!q) {
      // No query — show a curated quick-start set
      return staticItems.filter((i) =>
        ['m-dashboard', 'v-sales', 'v-payment', 'v-receipt', 'r-balancesheet', 'r-pnl', 'r-daybook', 'cm-ledger', 'lm-ledgers'].includes(i.id)
      );
    }

    const starts = allItems.filter((i) => i.label.toLowerCase().startsWith(q));
    const includes = allItems.filter((i) => !i.label.toLowerCase().startsWith(q) && i.label.toLowerCase().includes(q));

    return [...starts, ...includes].slice(0, 50);
  }, [query, dbLedgers, dbStockItems, staticItems, pushMenu]);

  const grouped = useMemo(() => {
    const order: ResultItem['category'][] = ['Ledgers', 'Stock Items', 'Masters', 'Vouchers', 'Reports', 'Menus'];
    const groups: { category: string; items: ResultItem[] }[] = [];
    order.forEach((cat) => {
      const items = results.filter((r) => r.category === cat);
      if (items.length) groups.push({ category: cat, items });
    });
    return groups;
  }, [results]);

  useEffect(() => {
    resultsRef.current = results;
    setActiveIndex(0);
  }, [results]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
    if (!isOpen) return;
    const active = listRef.current?.querySelector(`[data-idx="${activeIndex}"]`);
    if (active) (active as HTMLElement).scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  const runItem = (item: ResultItem) => {
    item.run();
    close();
  };

  if (!isOpen) return null;

  let runningIdx = -1;

  return (
    <div
      className="fixed inset-0 z-[3000] flex items-start justify-center pt-[12vh] bg-black/30 backdrop-blur-sm font-sans"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        className="w-[600px] max-w-[92vw] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
        style={{ maxHeight: '70vh' }}
      >
        {/* Search input */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <Search className="w-[18px] h-[18px] text-[#1e8585] flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search ledgers, stock items, vouchers, reports, menus..."
            className="flex-1 outline-none text-[14px] text-slate-700 placeholder:text-slate-400 bg-transparent"
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="button"
            onClick={close}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Results */}
        <div ref={listRef} className="overflow-y-auto" style={{ maxHeight: 'calc(70vh - 88px)' }}>
          {grouped.length === 0 ? (
            <div className="px-4 py-8 text-center text-slate-400 text-[13px]">No matches found</div>
          ) : (
            grouped.map((group) => (
              <div key={group.category} className="py-1.5">
                <div className="px-4 pt-1 pb-1 text-[10.5px] font-bold uppercase tracking-wide text-slate-400">
                  {group.category}
                </div>
                {group.items.map((item) => {
                  runningIdx += 1;
                  const idx = runningIdx;
                  const isActive = idx === activeIndex;
                  const Icon = CATEGORY_ICONS[item.category] || ArrowUpRight;
                  return (
                    <div
                      key={item.id}
                      data-idx={idx}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onMouseDown={(e) => { e.preventDefault(); runItem(item); }}
                      className={`flex items-center gap-2.5 px-4 py-2 cursor-pointer text-[13px] ${
                        isActive ? 'bg-[#f0f9f9] text-[#1e8585]' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className={`w-[15px] h-[15px] flex-shrink-0 ${isActive ? 'text-[#1e8585]' : 'text-slate-400'}`} />
                      <span className="flex-1 truncate font-medium">{item.label}</span>
                      {item.sublabel && (
                        <span className="text-[10.5px] text-slate-400 flex-shrink-0">{item.sublabel}</span>
                      )}
                      {isActive && <CornerDownLeft className="w-3.5 h-3.5 text-[#1e8585] flex-shrink-0" />}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center bg-[#fafafa] border-t border-slate-100 px-4 py-1.5 text-[11px] text-slate-400">
          <span><kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded text-[10px]">↑↓</kbd> Navigate</span>
          <span><kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded text-[10px]">Enter</kbd> Open</span>
          <span><kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded text-[10px]">Esc</kbd> Close</span>
        </div>
      </div>
    </div>
  );
};
